import { Button, Flex, Group, Pagination, Paper, Select } from '@mantine/core'
import { useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { ErrorWithReload } from '@/components/ui-shared/ErrorWithReload/ErrorWithReload'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import {
  useGetCrossCameraIdentityList,
  useGetCrossCameraPipelineList
} from '@/queries/crossCameraQueries'
import {
  CrossCameraDeleteTarget,
  DELETED_PIPELINES_ID
} from '@/types/crossCamera'
import { CrudAction, Resource } from '@/types/permissions'
import {
  MAX_ITEMS_WITHOUT_PAGINATION,
  getPageCount,
  getPageOffset
} from '@/utils/pagination'
import { useTableState } from '../ui-shared/Tables/useTableState'
import { CrossCameraIdentityList } from './CrossCameraIdentityList/CrossCameraIdentityList'
import { CrossCameraPersonDeleteModal } from './CrossCameraPersonDeleteModal/CrossCameraPersonDeleteModal'

const PAGE_SIZE = 20

export const CrossCameraPeoplePage = () => {
  const intl = useIntl()
  const [tableState, setTableState] = useTableState({ activePage: 1 })
  const { activePage } = tableState
  const [pipelineId, setPipelineId] = useState('')
  const [toDelete, setToDelete] = useState<CrossCameraDeleteTarget | null>(null)
  const [canDelete] = useHasPermissions(Resource.Applications, [
    CrudAction.Update
  ])

  const {
    data: pipelineData,
    isError: isPipelineError,
    error: pipelineError,
    refetch: refetchPipelines
  } = useGetCrossCameraPipelineList({
    limit: MAX_ITEMS_WITHOUT_PAGINATION,
    ordering: 'name'
  })
  const pipelines = useMemo(() => pipelineData?.results ?? [], [pipelineData])
  const selected = pipelines.find((pipeline) => pipeline.id === pipelineId)

  useEffect(() => {
    if (!pipelineId && pipelineData) {
      setPipelineId(pipelines[0]?.id ?? DELETED_PIPELINES_ID)
    }
  }, [pipelineId, pipelineData, pipelines])

  const { data, isLoading, isError, error, refetch, isPlaceholderData } =
    useGetCrossCameraIdentityList(pipelineId, {
      offset: getPageOffset(activePage, PAGE_SIZE),
      limit: PAGE_SIZE
    })

  const totalPageCount = getPageCount(data?.count ?? 0, PAGE_SIZE)

  useEffect(() => {
    if (totalPageCount > 0 && activePage > totalPageCount) {
      setTableState({ activePage: totalPageCount })
    }
  }, [activePage, totalPageCount, setTableState])

  const handleDeleteAllClick = () => setToDelete({ everyone: true })

  const handlePipelineChange = (value: string | null) => {
    setPipelineId(value ?? '')
    setTableState({ activePage: 1 })
  }

  if (isPipelineError) {
    return (
      <ErrorWithReload onReload={() => void refetchPipelines()}>
        {extractErrorMessage(
          pipelineError,
          intl.formatMessage({ id: 'crossCamera.pipelines.error' })
        )}
      </ErrorWithReload>
    )
  }

  return (
    <>
      <Paper p="sm" mb="md" bg="gray.0" withBorder>
        <Group justify="space-between" align="flex-end">
          <Select
            label={<FormattedMessage id="crossCamera.people.pipeline" />}
            placeholder={intl.formatMessage({
              id: 'crossCamera.people.pipelinePlaceholder'
            })}
            data={[
              ...pipelines.map((pipeline) => ({
                value: pipeline.id,
                label: pipeline.name
              })),
              {
                value: DELETED_PIPELINES_ID,
                label: intl.formatMessage({
                  id: 'crossCamera.people.deletedPipelines'
                })
              }
            ]}
            value={pipelineId}
            w={320}
            allowDeselect={false}
            onChange={handlePipelineChange}
          />
          {canDelete && (
            <Button
              variant="outline"
              color="red"
              disabled={isPlaceholderData || !data?.count}
              onClick={handleDeleteAllClick}
            >
              <FormattedMessage id="crossCamera.people.deleteAll" />
            </Button>
          )}
        </Group>
      </Paper>

      <CrossCameraIdentityList
        identities={data?.results ?? []}
        isLoading={isLoading}
        hasError={isError}
        errorMessage={
          isError
            ? extractErrorMessage(
                error,
                intl.formatMessage({ id: 'crossCamera.people.error' })
              )
            : undefined
        }
        onReload={() => void refetch()}
        onDeleteClick={
          canDelete
            ? (identityId, appId) => setToDelete({ identityId, appId })
            : undefined
        }
      />

      <Flex justify="end" mt="lg">
        <Pagination
          size="sm"
          total={totalPageCount}
          value={activePage}
          onChange={(value) => setTableState({ activePage: value })}
        />
      </Flex>

      <CrossCameraPersonDeleteModal
        appId={selected?.application.id}
        target={toDelete}
        onClose={() => setToDelete(null)}
      />
    </>
  )
}
